import { gql } from '@apollo/client';
import React, { useEffect, useMemo, useCallback } from 'react';
import ChevronRight from '@moda/icons/chevron-right-20';
import ChevronLeft from '@moda/icons/chevron-left-20';
import { Clickable, Constrain, Stack } from '@moda/om';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import loadable from '@loadable/component';
import { compact } from '@moda/portal-stanchions';
import { ResponsiveImage } from '../../../components/ResponsiveImage';
import { VariantCell, VARIANT_CELL_FRAGMENT, Grid } from '../../../components/Grid';
import { DesktopLookviewPageFragment } from '../../../generated/types';
import { useAdminMode } from '../../../components/AdminMode/AdminModeProvider';
import { Loading } from '../../../components/Loading';
import { DesktopLookviewPageLooksCarousel } from './DesktopLookviewPageLooksCarousel';
import { DesktopLookviewPagePrevIcon } from './DesktopLookviewPagePrevIcon';

import './DesktopLookviewPage.scss';

const AdminModeLookOverlay = loadable(
  () => import('../../../components/AdminMode/AdminModeLookOverlay'),
  {
    fallback: <Loading />
  }
);

export const DESKTOP_LOOKVIEW_PAGE_FRAGMENT = gql`
  fragment DesktopLookviewPageFragment on Look {
    id
    name
    trunkshowName: trunkshow_name
    primaryImageUrls: primary_image_urls {
      raw
      mediumLow: medium_low
    }
    variants {
      edges {
        node {
          id
          ...VariantCellFragment
        }
      }
    }
  }
  ${VARIANT_CELL_FRAGMENT}
`;

export type DesktopLookviewPageProps = {
  looks: DesktopLookviewPageFragment[];
};

export const DesktopLookviewPage: React.FC<DesktopLookviewPageProps> = ({ looks }) => {
  const history = useHistory();
  const location = useLocation();
  const { enabled: adminModeEnabled } = useAdminMode();

  const { lookId } = useParams<{
    lookId: string;
  }>();

  const queryString = location.search;
  const numberOfLooks = looks.length;
  const lookIndex = useMemo(() => looks.findIndex(({ id }) => id === lookId), [lookId, looks]);
  const look = useMemo(() => looks[lookIndex], [lookIndex, looks]);

  if (!look) {
    throw new Error('Not found');
  }

  const variants = useMemo(
    () => compact(compact(look.variants?.edges).map(({ node }) => node)),
    [look.variants]
  );

  const handleLookClick = useCallback(
    (lookId: string) => {
      history.push(lookId);
    },
    [history]
  );

  const handlePrevLook = useCallback(() => {
    const newLookId = lookIndex === 0 ? looks[numberOfLooks - 1].id : looks[lookIndex - 1].id;

    history.push(`${newLookId}${queryString}`);
  }, [history, lookIndex, looks, numberOfLooks, queryString]);

  const handleNextLook = useCallback(() => {
    const newLookId = lookIndex >= numberOfLooks - 1 ? looks[0].id : looks[lookIndex + 1].id;

    history.push(`${newLookId}${queryString}`);
  }, [history, lookIndex, looks, numberOfLooks, queryString]);

  const handleKeydown = useCallback(
    ({ key }: KeyboardEvent) => {
      switch (key) {
        case 'ArrowRight':
          handleNextLook();
          break;
        case 'ArrowLeft':
          handlePrevLook();
          break;
      }
    },
    [handleNextLook, handlePrevLook]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeydown);
    return (): void => window.removeEventListener('keydown', handleKeydown);
  }, [handleKeydown]);

  return (
    <div className="DesktopLookviewPage">
      <Constrain className="DesktopLookviewPage__container">
        <section>
          <Stack direction="horizontal" space={9}>
            <Stack className="DesktopLookviewPage__viewer" space={2}>
              {adminModeEnabled && (
                <AdminModeLookOverlay lookId={look.id} imageSrc={look.primaryImageUrls?.raw} />
              )}

              <ResponsiveImage
                className="DesktopLookviewPage__image"
                key={lookId}
                maxWidth={459}
                maxHeight={733}
                src={look.primaryImageUrls?.raw || '//:0'}
                alt={look.trunkshowName || 'Look'}
              />

              <DesktopLookviewPageLooksCarousel looks={looks} handleLookClick={handleLookClick} />
            </Stack>

            <Stack className="DesktopLookviewPage__details" space={10}>
              <Stack direction="horizontal" space={2} justifyContent="space-between">
                <Stack space={2} direction="horizontal">
                  <DesktopLookviewPagePrevIcon />

                  <Stack space={2}>
                    <h1 className="DesktopLookviewPage__trunkshow-name">{look.trunkshowName}</h1>

                    <span className="DesktopLookviewPage__looks-info">
                      Look {lookIndex + 1} / {numberOfLooks}
                    </span>
                  </Stack>
                </Stack>

                <nav className="DesktopLookviewPage__nav">
                  <Clickable onClick={handlePrevLook}>
                    <ChevronLeft height="24px" width="24px" />
                  </Clickable>

                  <Clickable onClick={handleNextLook}>
                    <ChevronRight height="24px" width="24px" />
                  </Clickable>
                </nav>
              </Stack>

              <Grid className="DesktopLookviewPage__variants">
                {variants.map((variant, index) => (
                  <VariantCell key={variant.id} variant={variant} selfPosition={index} />
                ))}
              </Grid>
            </Stack>
          </Stack>
        </section>
      </Constrain>
    </div>
  );
};
